import React, { memo, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import styles from './styles.module.css';
import { Table, Loading } from '../../../../../common/ui';
import { PiSmileySad } from 'react-icons/pi';
import { IoEyeOutline } from 'react-icons/io5';
import { formatOrderTypeLabel } from '../../utils/orderTypeLabels';

const formatPaymentMethods = (item = {}) => {
  const payments = Array.isArray(item.payments) ? item.payments : [];
  if (payments.length) {
    const label = payments
      .map((payment) => payment?.paymentMethodType)
      .filter(Boolean)
      .join(', ');
    if (label) {
      return label;
    }
  }
  return item.paymentMethod || '-';
};

const BillHistoryTable = memo(
  ({
    data = [],
    isRestaurantCompany = false,
    isLoading = false,
    error = null,
    pagination = {},
    setSearchParams,
    page = 1,
    limit = 10,
  }) => {
    const navigate = useNavigate();

    const formatCurrency = (value) => {
      if (value === null || value === undefined || value === '') return '0.00';
      const num = Number(value);
      if (isNaN(num)) return '0.00';
      return num.toFixed(2);
    };

    const formatDate = (dateValue) => {
      if (!dateValue) return '';
      try {
        const date = new Date(dateValue);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch (err) {
        return dateValue;
      }
    };

    const toMoney = (v) => Math.round(Number(v || 0) * 100) / 100;

    const mapStatusToLabel = (status, isFullyReturned) => {
      const normalizedStatus = String(status || '').trim().toUpperCase();

      if (!normalizedStatus) return 'ACTIVE';
      if (normalizedStatus === 'VOID') return 'VOID';
      if (normalizedStatus === 'RETURNED') {
        return isFullyReturned ? 'RETURNED' : 'PARTIALLY RETURNED';
      }
      if (normalizedStatus === 'COMPLETED') {
        return isFullyReturned
          ? 'RETURNED & CLAIMED'
          : 'PARTIALLY RETURNED & CLAIMED';
      }
      if (normalizedStatus === 'MONEY_REFUNDED') {
        return isFullyReturned ? 'REFUNDED' : 'PARTIALLY REFUNDED';
      }
      return '';
    };

    const getBillStatusLabel = (item = {}) => {
      const status = item?.returnStatus;
      const payableAmount = Number(item?.payableAmount || 0);
      const returnAmount = Number(item?.return_amount || 0);
      const isFullyReturned =
        toMoney(payableAmount) === toMoney(returnAmount);

      if (status === null || status === undefined) {
        return mapStatusToLabel('', isFullyReturned);
      }

      const splitStatuses = String(status)
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
      if (splitStatuses.length > 1) {
        return (
          <span>
            {splitStatuses.map((itemStatus, index) => (
              <span key={`${itemStatus}-${index}`}>
                {mapStatusToLabel(itemStatus, isFullyReturned)}
                {index < splitStatuses.length - 1 ? (
                  <span style={{ fontWeight: 700, color: '#1f1f1f' }}>, </span>
                ) : null}
              </span>
            ))}
          </span>
        );
      }

      return mapStatusToLabel(status, isFullyReturned);
    };

    const handleViewDetails = useCallback(
      (billId) => {
        navigate(`/reports/bill-history/${billId}`);
      },
      [navigate]
    );

    const transformedData = useMemo(() => {
      return (data || []).map((item) => ({
        id: item.id,
        billNo: item.receiptNo || item.billNo || '-',
        cashier_name: item.cashier_name || '-',
        customer_name: item.customer_name || '-',
        orderType: formatOrderTypeLabel(item.orderType),
        tableLabel: item.tableLabel || '-',
        paymentMethod: formatPaymentMethods(item),
        totalAmount: formatCurrency(item.totalAmount) - formatCurrency(item.serviceChargeAmount),
        totalDiscount: formatCurrency(item.totalDiscount),
        serviceChargeAmount: formatCurrency(item.serviceChargeAmount),
        payableAmount: formatCurrency(item.payableAmount),
        paidAmount: formatCurrency(item.paidAmount),
        cost: formatCurrency(item.cost),
        profit: formatCurrency(item.profit),
        createdDate: formatDate(item.createdDate),
        status: getBillStatusLabel(item),
        _raw: item,
      }));
    }, [data]);

    const handleChangePage = (event, newPage) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('page', String(newPage + 1));
        return next;
      });
    };

    const handleChangeRowsPerPage = (event) => {
      const newLimit = parseInt(event?.target?.value, 10) || 10;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('limit', String(newLimit));
        next.set('page', '1');
        return next;
      });
    };

    const columns = useMemo(() => {
      const base = [
        { id: 'billNo', label: 'Bill No', minWidth: 80 },
        { id: 'cashier_name', label: 'Cashier', minWidth: 120 },
        { id: 'customer_name', label: 'Customer', minWidth: 150 },
      ];
      if (isRestaurantCompany) {
        base.push(
          { id: 'orderType', label: 'Order Type', minWidth: 100 },
          { id: 'tableLabel', label: 'Table', minWidth: 100 }
        );
      }
      base.push(
        { id: 'paymentMethod', label: 'Payment Method', minWidth: 120 },
        { id: 'totalAmount', label: 'Total', minWidth: 60 },
        { id: 'totalDiscount', label: 'Discount', minWidth: 60 }
      );
      if (isRestaurantCompany) {
        base.push({
          id: 'serviceChargeAmount',
          label: 'Service Charge',
          minWidth: 80,
        });
      }
      base.push(
        { id: 'payableAmount', label: 'Payable', minWidth: 60 },
        { id: 'paidAmount', label: 'Paid', minWidth: 60 },
        { id: 'cost', label: 'Cost', minWidth: 60 },
        { id: 'profit', label: 'Profit', minWidth: 60 },
        { id: 'createdDate', label: 'Created At', minWidth: 160 },
        { id: 'status', label: 'Status', minWidth: 140 },
        {
          id: 'actions',
          label: 'Actions',
          minWidth: 60,
          render: (_, row) => (
            <button
              type="button"
              className={styles.viewBtn}
              onClick={() => handleViewDetails(row.id)}
              title="View Details"
              aria-label="View bill details"
            >
              <IoEyeOutline size={18} />
            </button>
          ),
        }
      );
      return base;
    }, [isRestaurantCompany, handleViewDetails]);

    const totalCount = pagination?.totalRecords ?? 0;

    if (isLoading) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.loadingWrap}>
            <Loading variant="inline" label="" />
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.noData}>
            <PiSmileySad className={styles.noDataIcon} aria-hidden />
            <p>Error loading data: {error}</p>
          </div>
        </div>
      );
    }

    if (!transformedData.length) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.noData}>
            <PiSmileySad className={styles.noDataIcon} aria-hidden />
            <p>No bills found</p>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.wrapper}>
        <Table
          columns={columns}
          rows={transformedData}
          page={page - 1}
          rowsPerPage={limit}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          totalCount={totalCount}
          disableInternalPagination
        />
      </div>
    );
  }
);

BillHistoryTable.propTypes = {
  data: PropTypes.array,
  isRestaurantCompany: PropTypes.bool,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  pagination: PropTypes.object,
  setSearchParams: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
};

export default BillHistoryTable;
